import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, ViewChild, TemplateRef, AfterViewInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs';
import { ButtonType, ActionRole, ModalTitleSize, ModalSize } from '@newroom-connect/library/enums';
import { ArrayHelper, StringHelper } from '@newroom-connect/library/helpers';

import { ButtonComponent } from '../buttons/button/button.component';
import { IconComponent } from '../icon/icon.component';
import { DestroyableComponent } from '../abstract/destroyable/destroyable.component';
import { InputLanguageSelectorComponent, InputLanguageSelectorComponentType } from '../inputs/input-language-selector/input-language-selector.component';
import { InputCheckboxComponent } from '../inputs/input-checkbox/input-checkbox.component';
import { ModalComponent, ModalService } from '../modal/modal.component';

interface IAutoTranslateForm {
  sourceLanguage: FormControl<string>;
  targetLanguage: FormControl<string>;
  // File translation case
  fileId?: FormControl<string | null>;
  fileName?: FormControl<string | null>;
  exportAsPdf?: FormControl<boolean | null>;
  // Text translation case
  text?: FormControl<string | null>;
  // Key-value translation case
  keyValue?: FormControl<{ [key: string]: string } | null>;
}

export interface IAutoTranslateParams {
  sourceLanguage: string;
  targetLanguage: string;
  fileId?: string | null;
  fileName?: string | null;
  exportAsPdf?: boolean | null;
  // Text translation case
  text?: string | null;
  // Key-value translation case
  keyValue?: { [key: string]: string } | null;
}

export interface IModalTexts {
  title: string;
  description: string;
  subline: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IconComponent,
    ButtonComponent,
    InputCheckboxComponent,
    InputLanguageSelectorComponent
  ],
  selector: 'nrc-auto-translate',
  templateUrl: './auto-translate.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutoTranslateComponent extends DestroyableComponent implements AfterViewInit {
  private readonly modalService = inject(ModalService);

  @ViewChild('autoTranslateModalContent') public autoTranslateModalContent!: TemplateRef<HTMLElement>;

  @Input({ required: true }) public selectedLanguageCode!: string;
  @Input({ required: true }) public languageCodes: string[] = [];
  @Input({ required: true }) public modalTexts: IModalTexts = {
    title: 'Auto Translate',
    description: 'Translate your text or files automatically',
    subline: 'Select the languages you want to translate to and from'
  };

  @Input() public keyValueTranslation?: { [key: string]: string };
  @Input() public textTranslation?: string;
  @Input() public fileId?: string;
  @Input() public fileName?: string;
  @Input() public exportAsPdf?: boolean;

  @Output() public autoTranslateResult = new EventEmitter<IAutoTranslateParams>();
  @Output() public closeEvent = new EventEmitter<void>();

  public autoTranslateFormGroup = new FormGroup<IAutoTranslateForm>({
    sourceLanguage: new FormControl<string>('', { nonNullable: true }),
    targetLanguage: new FormControl<string>('', { nonNullable: true }),
    fileId: new FormControl<string | null>(null),
    fileName: new FormControl<string | null>(null),
    exportAsPdf: new FormControl<boolean>(false),
    text: new FormControl<string | null>(null),
    keyValue: new FormControl<{ [key: string]: string } | null>(null)
  });

  public isTranslateModalOpen = false;
  public sourceLanguageCodes: string[] = [];
  public targetLanguageCodes: string[] = [];

  public buttonType = ButtonType;
  public actionRole = ActionRole;
  public inputLanguageSelectorComponentType = InputLanguageSelectorComponentType;
  public modalTitleSize = ModalTitleSize;

  private modalId = '';

  /**
   *
   */
  public ngAfterViewInit(): void {
    this.sourceLanguageCodes = [...this.languageCodes];
    this.targetLanguageCodes = [...this.languageCodes];

    this.modalId = `AutoTranslateModal-${StringHelper.generateRandomString(10)}`;

    if (!ArrayHelper.isNotEmpty(this.sourceLanguageCodes) || !ArrayHelper.isNotEmpty(this.targetLanguageCodes)) {
      return;
    }

    this.autoTranslateFormGroup.patchValue({
      sourceLanguage: this.selectedLanguageCode,
      targetLanguage: this.targetLanguageCodes.at(0),
      fileId: this.fileId,
      fileName: this.fileName,
      exportAsPdf: this.exportAsPdf,
      text: this.textTranslation,
      keyValue: this.keyValueTranslation
    });

    const modalRef = this.modalService.open({
      component: ModalComponent,
      inputs: {
        id: this.modalId,
        size: ModalSize.MEDIUM,
        title: '',
        paddedContent: true
      },
      content: this.autoTranslateModalContent
    });

    modalRef.instance.closeEvent.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.modalService.close(this.modalId);
      this.closeEvent.emit();
    });

    this.registerSourceLanguageCodeFormChange();
  }

  /**
   * Sends back the selected language codes (from & to).
   *
   */
  public async handleAutoTranslateEvent(): Promise<void> {
    this.autoTranslateResult.emit({
      sourceLanguage: this.autoTranslateFormGroup.controls.sourceLanguage.value,
      targetLanguage: this.autoTranslateFormGroup.controls.targetLanguage.value,
      fileId: this.autoTranslateFormGroup.controls.fileId?.value,
      fileName: this.autoTranslateFormGroup.controls.fileName?.value,
      exportAsPdf: this.autoTranslateFormGroup.controls.exportAsPdf?.value,
      text: this.autoTranslateFormGroup.controls.text?.value,
      keyValue: this.autoTranslateFormGroup.controls.keyValue?.value
    });

    this.modalService.close(this.modalId);
    this.closeEvent.emit();
  }

  /**
   * Watches the changes in from languageCode and sets the to languageCode.
   */
  private registerSourceLanguageCodeFormChange(): void {
    this.autoTranslateFormGroup.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.sourceLanguageCodes = [...this.languageCodes.filter(code => code !== value.targetLanguage)];
        this.targetLanguageCodes = [...this.sourceLanguageCodes.filter(code => code !== value.sourceLanguage)];
      });
  }
}
